import type {
    IEventEmitter,
    EventMap,
    ISubscriber,
} from '@src/slide/infrastructure/module/independent/event-emitter/event-emitter.interface';

/**
 * 基于常见的发布订阅机制实现的简易EventEmitter，基于泛型实现了完整的 ts 约束
 * 不用 eventemitter 一些第三方包的原因：
 *  1.这些对 ts 的支持都不能直接满足根据泛型 EventMap 来做校验的需求。
 *  2.只为了 ts 做一层 proxy 转发又没太大必要，emitter 本身逻辑并不复杂。
 *
 * 后续如果有 Once 等 api 的需求，可以直接新增。或者是在复杂，再考虑用第三方包
 */
export class EventEmitter<E extends EventMap> implements IEventEmitter<E> {
    private subscribers: { [K in keyof E]?: Array<ISubscriber<E, K>> } = {};
    private pastEvents: { [K in keyof E]?: boolean } = {};

    /**
     * 注册监听函数，注册时已错过的emit不再触发
     * @param event
     * @param subscriber
     */
    public on<K extends keyof E>(event: K, subscriber: ISubscriber<E, K>): () => void {
        if (!this.subscribers[event]) {
            this.subscribers[event] = [];
        }

        this.subscribers[event]!.push(subscriber);
        return () => {
            this.off(event, subscriber);
        };
    }

    /**
     * 注册监听函数，注册时已错过的emit也会触发一次（注意，为了避免内存泄漏，监听函数不会回传任何数据）
     * @param event
     * @param subscriber
     */
    public onIncludingPast<K extends keyof E>(event: K, subscriber: () => void): () => void {
        if (!this.subscribers[event]) {
            this.subscribers[event] = [];
        }

        this.subscribers[event]!.push(subscriber);
        if (this.pastEvents[event]) {
            subscriber();
        }
        return () => {
            this.off(event, subscriber);
        };
    }

    public once<K extends keyof E>(event: K, subscriber: ISubscriber<E, K>): void {
        const onceCallback = (payload?: E[K]) => {
            subscriber.apply(this, [payload]);
            this.off(event, onceCallback);
        };
        this.on(event, onceCallback);
    }

    public off<K extends keyof E>(event: K, subscriber: ISubscriber<E, K>): void {
        if (this.subscribers[event]) {
            this.subscribers[event] = this.subscribers[event]!.filter(s => s !== subscriber);
        }
    }

    public emit<K extends keyof E>(event: K, payload?: E[K]): void {
        this.pastEvents[event] = true;
        if (this.subscribers[event]) {
            this.subscribers[event]!.forEach((subscriber) => {
                try {
                    subscriber.apply(this, [payload]);
                } catch (err) {
                    window.log.error('[EventEmitter]', err);
                }
            });
        }
    }

    public getPastEvent<K extends keyof E>(event: K): boolean {
        return !!this.pastEvents[event];
    }
}
